import { useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

interface PortfolioProps {
  isDarkMode: boolean;
}

// Curated 4-piece preview for the homepage — full archive lives on /portfolio
const FEATURED = [
  {
    id: 'p1',
    num: '01',
    title: 'CleanVault',
    accent: 'iOS · Android',
    meta: 'System Cleaner',
    year: '2026',
    aspect: 'aspect-[4/5]',
    span: 'md:col-span-7',
    row: 'a',
    src: '/tools-1.webp',
  },
  {
    id: 'p2',
    num: '02',
    title: 'DupliScan',
    accent: 'macOS',
    meta: 'Duplicate Finder',
    year: '2025',
    aspect: 'aspect-[4/5]',
    span: 'md:col-span-5',
    row: 'a',
    src: '/tools-2.webp',

  },
  {
    id: 'p3',
    num: '03',
    title: 'WriteFlow AI',
    accent: 'Web · API',
    meta: 'AI Writing Assistant',
    year: '2026',
    aspect: 'aspect-[4/3]',
    span: 'md:col-span-8',
    row: 'b',
    src: '/tools-3.webp',
  },
  {
    id: 'p4',
    num: '04',
    title: 'BoostKit',
    accent: 'iOS',
    meta: 'Performance Optimiser',
    year: '2025',
    aspect: 'aspect-[3/4]',
    span: 'md:col-span-4',
    row: 'b',
    src: '/tools-4.webp',

  },
];

const Portfolio = ({ isDarkMode }: PortfolioProps) => {
  const sectionRef = useRef<HTMLElement>(null);
  const [hovered, setHovered] = useState<string | null>(null);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const animEls = sectionRef.current?.querySelectorAll('[data-anim]') ?? [];
      const workEls = sectionRef.current?.querySelectorAll('[data-work]') ?? [];

      if (animEls.length) {
        gsap.fromTo(
          animEls,
          { opacity: 0, y: 30 },
          {
            opacity: 1,
            y: 0,
            duration: 1,
            ease: 'power3.out',
            stagger: 0.08,
            scrollTrigger: { trigger: sectionRef.current, start: 'top 80%', once: true },
          }
        );
      }
      if (workEls.length) {
        gsap.fromTo(
          workEls,
          { opacity: 0, y: 60 },
          {
            opacity: 1,
            y: 0,
            duration: 1.1,
            ease: 'power3.out',
            stagger: 0.09,
            scrollTrigger: { trigger: sectionRef.current, start: 'top 70%', once: true },
          }
        );
      }
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  // Group works by editorial row (two compact rows for the preview)
  const rows = Array.from(new Set(FEATURED.map((w) => w.row))).map((r) =>
    FEATURED.filter((w) => w.row === r)
  );

  return (
    <section
      ref={sectionRef}
      id="portfolio"
      className={`relative w-full py-24 md:py-32 px-6 md:px-12 transition-colors duration-700 ${
        isDarkMode ? 'bg-[#050505] text-white' : 'bg-[#f5f5f5] text-black'
      }`}
    >
      <div className="max-w-7xl mx-auto">
        {/* ───── Compact header — single row, editorial ───── */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-14 md:mb-20">
          <div className="max-w-2xl">
            <div
              data-anim
              className={`mb-6 inline-flex items-center gap-2 px-3 py-1 rounded-full border text-[11px] font-mono uppercase tracking-[0.2em] ${
                isDarkMode
                  ? 'border-white/20 text-white/70 bg-white/5'
                  : 'border-black/15 text-black/60 bg-black/5'
              }`}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-current" />
              02 — Products · 2024–2026
            </div>
            <h2
              data-anim
              className="text-[2.5rem] md:text-[4.25rem] leading-[0.95] font-bold tracking-tighter"
            >
              Tools built for{' '}
              <span className="italic font-light">real impact</span>
              <span className={isDarkMode ? 'text-white/30' : 'text-black/30'}>.</span>
            </h2>
          </div>

          <div className="flex flex-col gap-4 md:items-end">
            <p
              data-anim
              className={`max-w-sm text-sm md:text-[15px] leading-relaxed md:text-right ${
                isDarkMode ? 'text-white/60' : 'text-black/60'
              }`}
            >
              A selection from our product portfolio —{' '}
              <em className="italic font-normal">utility apps</em> shipped to millions of users worldwide.
            </p>
            <Link
              to="/portfolio"
              data-anim
              className={`group inline-flex items-center gap-2 text-sm font-medium cursor-pointer transition-opacity hover:opacity-70 ${
                isDarkMode ? 'text-white' : 'text-black'
              }`}
            >
              <span className="border-b border-current pb-0.5">View all products</span>
              <i className="ri-arrow-right-up-line text-base group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"></i>
            </Link>
          </div>
        </div>

        {/* ───── Two-row preview grid ───── */}
        <div
          className="space-y-6 md:space-y-8"
          onMouseLeave={() => setHovered(null)}
        >
          {rows.map((row, rowIdx) => (
            <div
              key={`row-${rowIdx}`}
              className="grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-6"
            >
              {row.map((work) => {
                const isDim = hovered !== null && hovered !== work.id;
                const isActive = hovered === work.id;
                return (
                  <figure
                    key={work.id}
                    data-work
                    onMouseEnter={() => setHovered(work.id)}
                    className={`group relative ${work.span ?? 'md:col-span-4'} transition-all duration-700 ease-out ${
                      isDim ? 'opacity-40 scale-[0.985]' : 'opacity-100 scale-100'
                    }`}
                  >
                    <Link
                      to="/portfolio"
                      className={`relative block ${work.aspect} overflow-hidden cursor-pointer ${
                        isDarkMode ? 'bg-[#0a0a0a]' : 'bg-[#e8e8e8]'
                      }`}
                    >
                      <img
                        src={work.src}
                        alt={`${work.title} ${work.accent} — ${work.meta} portrait by noir studio`}
                        title={`${work.title} — editorial portrait photography`}
                        loading="lazy"
                        className={`absolute inset-0 w-full h-full object-cover object-top transition-all duration-[1400ms] ease-out ${
                          isActive ? 'scale-[1.05]' : 'scale-100'
                        }`}
                      />
                      <div className="absolute inset-0 bg-gradient-to-b from-black/0 via-black/0 to-black/30 pointer-events-none" />

                      <span
                        className={`absolute top-3 left-3 px-2 py-1 text-[10px] font-mono tracking-[0.25em] backdrop-blur-md transition-all duration-500 ${
                          isDarkMode ? 'bg-black/40 text-white/80' : 'bg-white/60 text-black/75'
                        }`}
                      >
                        N° {work.num}
                      </span>

                      <div
                        className={`absolute bottom-4 right-4 w-9 h-9 flex items-center justify-center rounded-full bg-white text-black transition-all duration-500 ${
                          isActive
                            ? 'opacity-100 translate-x-0 translate-y-0'
                            : 'opacity-0 translate-x-1 translate-y-1'
                        }`}
                      >
                        <i className="ri-arrow-right-up-line text-sm"></i>
                      </div>
                    </Link>

                    <figcaption className="pt-3 flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm md:text-base font-semibold tracking-tight leading-tight">
                          {work.title}
                          <span
                            className={`italic font-light ml-1.5 ${
                              isDarkMode ? 'text-white/55' : 'text-black/50'
                            }`}
                          >
                            — {work.accent}
                          </span>
                        </h3>
                        <p
                          className={`mt-1 text-[10px] font-mono uppercase tracking-[0.22em] ${
                            isDarkMode ? 'text-white/45' : 'text-black/45'
                          }`}
                        >
                          {work.meta}
                        </p>
                      </div>
                      <span
                        className={`text-[10px] font-mono tracking-[0.2em] pt-1 shrink-0 ${
                          isDarkMode ? 'text-white/40' : 'text-black/40'
                        }`}
                      >
                        {work.year}
                      </span>
                    </figcaption>
                  </figure>
                );
              })}
            </div>
          ))}
        </div>

        {/* ───── Tail CTA strip ───── */}
        <div
          data-anim
          className={`mt-16 md:mt-20 pt-8 border-t flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5 ${
            isDarkMode ? 'border-white/10' : 'border-black/10'
          }`}
        >
          <p
            className={`text-sm md:text-[15px] ${
              isDarkMode ? 'text-white/55' : 'text-black/55'
            }`}
          >
            <span className="font-mono text-[11px] uppercase tracking-[0.25em] mr-3">
              Product line
            </span>
            24 apps · 8 platforms · spanning three years of development.
          </p>
          <Link
            to="/portfolio"
            className={`inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium hover:scale-105 transition-transform duration-300 whitespace-nowrap cursor-pointer ${
              isDarkMode
                ? 'bg-white text-black hover:bg-gray-200'
                : 'bg-black text-white hover:bg-gray-900'
            }`}
          >
            Explore products
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default Portfolio;